import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { SampleDeliveryRequestService } from '../../../services/sample/sample-delivery-request.service';
import { NotificationService, ToastStyle } from '../../../services/notification/notification.service';
import { IndSectionHeaderViewDataModel } from '../../../models/indSectionHeaderDataModel';
import { IONote } from '../../../classes/io/io-note.class';
import { StagedSdrNote } from '../../../data-services/sample/sample-delivery-request.data.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { DeviceService } from '@omni/services/device/device.service';
import { UIService } from '@omni/services/ui/ui.service';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';
import { IoFileService } from '@omni/services/io-file-service/io-file-service';
import {
  toBase64,
  MAXIMUM_NOTE_ATTACHMENT_SIZE,
  NOTE_ATTACHMENT_MIME_TYPES_SUPPORTED_REGEX,
  formatFileSize,
} from '@omni/utility/util';
import { NotesAttachmentsConfig } from '@omni/utility/notes-attachments-config.util';

export interface SdrNoteSavedPayload {
  noteId?: string;
  text: string;
  attachment?: {
    fileDataUrl: string;
    mimeType: string;
    name: string;
    size: number;
  };
  isAttachmentRemoved?: boolean;
}

@Component({
  selector: 'sdr-notes-section',
  templateUrl: 'sdr-notes-section.component.html',
  styleUrls: ['sdr-notes-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SdrNotesSectionComponent implements OnInit, OnChanges {
  @Input() sdrId: string | null = null;
  @Input() existingNotes: IONote[] = [];
  @Input() readOnly = false;
  /** Hides the "Notes and attachments" ind-section-header — the inline
   *  meeting-embedded SDR form doesn't want a separate section label above
   *  the composer (the placeholder text already says what the box is for);
   *  the standalone form and read-only detail view keep it (default true). */
  @Input() showSectionHeader = true;
  /**
   * Seeds the editable composer's staged-notes list at creation time, for a
   * parent resuming an existing (not-yet-submitted) SDR whose notes are
   * already real Dynamics annotations. Each entry is a plain StagedSdrNote
   * the PARENT already constructed (and is separately tracking the real
   * annotation id for, in its own noteServerIds map, keyed by this exact
   * object reference) — this component just appends them into `staged`/
   * `stagedNotesView` verbatim, giving them the same edit/delete affordances
   * as any note added in this session. Read once, at component creation
   * (ngOnInit) — not reactive to later changes (this input only ever matters
   * for the initial seed of a resumed draft).
   */
  @Input() initialStagedNotes: StagedSdrNote[] | null = null;

  /** Overrides the SDR notes config so other entities (e.g. meeting expenses) can reuse this section. */
  @Input() notesConfig: NotesAttachmentsConfig | null = null;

  @Output() noteSaved = new EventEmitter<SdrNoteSavedPayload>();
  @Output() noteDeleted = new EventEmitter<string>();
  /** Additive save-on-click hook — fired alongside noteSaved whenever a NEW
   *  staged note is committed via Add, carrying its staged-array index and the
   *  StagedSdrNote shape the SDR data service's upsertSdrNote/upsertSdrNoteOffline
   *  expect. Does not change noteSaved's existing payload/consumers. */
  @Output() stagedNoteAdded = new EventEmitter<{ index: number; note: StagedSdrNote }>();
  /** Additive save-on-click hook — fired alongside the existing local splice
   *  whenever a staged (not-yet-finalized) note is deleted, carrying its
   *  PRE-splice index. Distinct from noteDeleted (annotation-id based, used
   *  by the detail view for an already-persisted note) — this is index-based
   *  for a staged note that may or may not have reached Dynamics/local storage yet. */
  @Output() stagedNoteRemoved = new EventEmitter<number>();
  /** Additive save-on-click hook — fired whenever an ALREADY-STAGED note is
   *  edited via the pencil icon and saved. Without this the edit stayed purely
   *  local: add/remove were wired to autosave but an edit never was, so the
   *  pre-edit text is what reached Dynamics while the rep saw their new text
   *  on screen. `attachmentCleared` distinguishes "attachment explicitly
   *  removed" from "attachment untouched" — the note shape alone can't, since
   *  both leave `attachment` undefined. */
  @Output() stagedNoteEdited = new EventEmitter<{
    index: number;
    note: StagedSdrNote;
    attachmentCleared: boolean;
  }>();

  @ViewChild('attachInput') attachInput: ElementRef<HTMLInputElement>;
  @ViewChild('editAttachInput') editAttachInput: ElementRef<HTMLInputElement>;

  /** Whether the section renders at all (BU_ACTIVITY_GA_NOTES_ATTACHMENTS present). */
  isSectionVisible = false;
  /** Whether the note text input renders (notes key present). */
  showNoteInput = false;
  /** Whether the attach affordance renders (attachments key present). */
  isAttachmentEnabled = false;
  /** Whether a note (text) is required to submit the SDR. */
  notesMandatory = false;
  /** Whether an attachment is required to submit the SDR. */
  attachmentsMandatory = false;
  /**
   * Flipped when the user engages the note TEXT (types in the composer) or
   * commits via Add — gates the note-required helper independently of
   * attachments so typing a note never surfaces an attachment-required message.
   */
  touchedNotes = false;
  /**
   * Flipped when the user engages ATTACHMENTS — cancels the file picker without
   * choosing a file, removes a staged/saved attachment, or commits via Add.
   * Gates the attachment-required helper. Opening the picker alone does NOT
   * flip it (choosing a file satisfies the requirement; only cancelling leaves
   * it unmet), so the helper never flashes the instant Attach is tapped.
   */
  touchedAttachment = false;
  /**
   * Notes staged in the composer this session (create flow only). Persisted
   * via the SDR submit batch — the parent calls `getStagedNotes()` and feeds
   * the result through `sdrService.submit`.
   */
  staged: StagedSdrNote[] = [];

  /**
   * Same notes as `staged`, but shaped as `IONote` instances so the template
   * can render them with the same read-only entry-card markup used for
   * `existingNotes` (owner initials, timestamp, attachment row), plus Edit/
   * Delete icon controls since these aren't submitted yet. Kept in lockstep
   * with `staged` by array index (see `startEditStagedNote`/`deleteStagedNote`).
   */
  stagedNotesView: IONote[] = [];

  sectionHeader: IndSectionHeaderViewDataModel;
  newNoteText = '';
  stagedAttachment: { file: File; base64: string } | null = null;

  /** Index into `staged`/`stagedNotesView` currently open for inline text
   *  editing (pencil icon), or null when none is. */
  editingStagedIndex: number | null = null;
  editingStagedText = '';
  /**
   * Attachment state while editing a staged note: `undefined` = keep the
   * note's current attachment unchanged (the default on opening edit mode);
   * `null` = explicitly removed (× tapped, no replacement chosen yet);
   * an object = a newly picked replacement file.
   */
  editingStagedAttachment: { file: File; base64: string } | null | undefined = undefined;

  /** Placeholder shown in the note text-area. Sourced from the
   * `indskr_notessectionhelptext` BU config, falling back to ENTER_NOTES.
   * Resolved once in ngOnInit. */
  notesPlaceholder = '';

  constructor(
    private readonly sdrService: SampleDeliveryRequestService,
    private readonly translate: TranslateService,
    private readonly notificationService: NotificationService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly authService: AuthenticationService,
    private readonly deviceService: DeviceService,
    private readonly uiService: UIService,
    private readonly dateTimeFormatsService: DateTimeFormatsService,
    private readonly fileService: IoFileService,
    private readonly datePipe: DatePipe,
  ) {}

  private get resolvedConfig(): NotesAttachmentsConfig {
    return this.notesConfig ?? this.sdrService.getNotesConfig();
  }

  ngOnInit() {
    // Visibility, which inputs render, and mandatory-ness all come from the
    // BU_ACTIVITY_GA_NOTES_ATTACHMENTS iO config (replaces the former two FAs):
    //   {"notes":…}              → note text input only
    //   {"attachments":…}        → attach affordance only
    //   both keys present        → both
    // absent / unrecognised      → section hidden
    const config = this.resolvedConfig;
    this.isSectionVisible = this.computeVisibility(config);
    this.showNoteInput = config.showNotes;
    this.isAttachmentEnabled = config.showAttachments;
    this.notesMandatory = config.notesMandatory;
    this.attachmentsMandatory = config.attachmentsMandatory;

    // Help text mirrors the contact-info pattern: BU config wins, ENTER_NOTES is the fallback.
    const buHelp = this.authService.user?.buConfigs?.['indskr_notessectionhelptext'];
    this.notesPlaceholder = buHelp || this.translate.instant('ENTER_NOTES');

    this.buildSectionHeader();

    if (this.initialStagedNotes?.length) {
      for (const note of this.initialStagedNotes) {
        this.staged.push(note);
        this.stagedNotesView.push(this.toIoNoteView(note, this.staged.length - 1));
      }
      this.buildSectionHeader();
    }
  }

  /**
   * Read-mode header reactivity: edits to existingNotes (e.g. deleting the
   * last attachment-bearing note) can flip the derived title between
   * "Notes" / "Attachments" / "Notes and Attachments", or hide the section
   * entirely when the last record is removed. Edit-mode is invariant under
   * input changes — its title is config-only.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (this.readOnly && changes['existingNotes']) {
      const config = this.resolvedConfig;
      this.isSectionVisible = this.computeVisibility(config);
      this.buildSectionHeader();
    }
  }

  /**
   * Read mode hides the entire section when there are zero records; edit
   * mode follows the iO config only.
   */
  private computeVisibility(config: { visible: boolean }): boolean {
    if (!config.visible) return false;
    if (this.readOnly) return (this.existingNotes?.length ?? 0) > 0;
    return true;
  }

  /**
   * Builds the Notes section header (title + required asterisk only). Attach
   * and Add note live as a footer row inside the composer card, not as header
   * controls, so the card reads as one self-contained unit.
   */
  private buildSectionHeader() {
    this.sectionHeader = {
      id: 'sdr-notes-header',
      title: this.translate.instant(this.resolveTitleKey()),
      // Asterisk on the section header when the iO config marks notes mandatory.
      // Read-mode never shows an asterisk (no submit), so suppress it there.
      isRequired: (this.notesMandatory || this.attachmentsMandatory) && !this.readOnly,
      // Title contains the word "and" (e.g. "Notes and Attachments") that the
      // default ind-section-header capitalize-first-letter pipe would otherwise
      // title-case into "Notes And Attachments". Opt out of the case mangling.
      doNotModifyTitleCase: true,
      // "N entries" summary on the submitted (read-only) view only — the
      // editable composer has no equivalent concept.
      rightLabelTxt: this.readOnly && this.existingNotes?.length
        ? this.translate.instant('ENTRIES_COUNT', { count: this.existingNotes.length })
        : '',
      controls: [],
    };
  }

  /**
   * Edit mode → title from resolved iO config (showNotes / showAttachments).
   * Read mode → title scanned from `existingNotes` (per-record text +
   * attachment presence). See OMNI-57234 redesign spec §3.
   */
  private resolveTitleKey(): string {
    if (this.readOnly) {
      return this.sdrService.deriveNotesSectionTitleKey(this.existingNotes ?? []);
    }
    const config = this.resolvedConfig;
    return this.sdrService.getNotesSectionTitleKeyFromConfig(config);
  }

  get isSaveDisabled(): boolean {
    return !this.newNoteText?.trim() && !this.stagedAttachment;
  }

  /**
   * Whether any note TEXT is present anywhere — an existing note with text, a
   * staged note with text, or the live composer text. Drives the notes-mandatory
   * gate independently of attachments (§4.1 of the array-config spec).
   */
  get hasText(): boolean {
    return (
      (this.existingNotes ?? []).some((n) => !!n?.noteText?.trim()) ||
      this.staged.some((s) => !!s?.noteText?.trim()) ||
      !!this.newNoteText?.trim()
    );
  }

  /**
   * Whether any ATTACHMENT is present anywhere — an existing note with a
   * document, a staged note with an attachment, or a staged composer
   * attachment. Drives the attachments-mandatory gate independently of notes.
   */
  get hasAttachment(): boolean {
    return (
      (this.existingNotes ?? []).some((n) => !!n?.hasDocument) ||
      this.staged.some((s) => !!s?.attachment) ||
      !!this.stagedAttachment
    );
  }

  /**
   * Returns the notes staged for the create submit batch (text + optional
   * attachment). Called by the parent immediately before submit so anything
   * still in the composer that the user hasn't tapped "Save" on yet also rides
   * the batch (no point making the user click twice).
   */
  getStagedNotes(): StagedSdrNote[] {
    const out: StagedSdrNote[] = [...this.staged];
    if (this.newNoteText?.trim() || this.stagedAttachment) {
      out.push(this.composerToStaged(this.newNoteText, this.stagedAttachment));
    }
    return out;
  }

  private composerToStaged(text: string, file: { file: File; base64: string } | null): StagedSdrNote {
    const note: StagedSdrNote = { noteText: (text ?? '').trim() };
    if (file) {
      note.attachment = {
        documentBody: file.base64,
        fileName: file.file.name,
        mimeType: file.file.type,
        fileSize: file.file.size,
      };
    }
    return note;
  }

  /** A mandatory note requirement is unmet (no text present). */
  get notesUnmet(): boolean {
    return this.notesMandatory && !this.hasText;
  }

  /** A mandatory attachment requirement is unmet (no attachment present). */
  get attachmentsUnmet(): boolean {
    return this.attachmentsMandatory && !this.hasAttachment;
  }

  /**
   * Note-required helper — shown only once the user has engaged the note text
   * (typed or committed via Add) and the note requirement is still unmet.
   * Gated on `touchedNotes` alone so it is independent of attachment state.
   */
  get showNotesError(): boolean {
    return !this.readOnly && this.touchedNotes && this.notesUnmet;
  }

  /**
   * Attachment-required helper — shown only once the user has engaged
   * attachments (cancelled the picker, removed an attachment, or committed via
   * Add) and the attachment requirement is still unmet. Gated on
   * `touchedAttachment` so merely typing a note — or merely opening the file
   * picker — never surfaces it.
   */
  get showAttachmentError(): boolean {
    return !this.readOnly && this.touchedAttachment && this.attachmentsUnmet;
  }

  /** Red helper visible when either per-type requirement is touched-and-unmet. */
  get isNotesInvalid(): boolean {
    return this.showNotesError || this.showAttachmentError;
  }

  /**
   * Message for the INLINE helper — resolved from which requirement(s) are
   * currently *surfaced* (touched-and-unmet), so the wording matches exactly
   * what the user has engaged. Distinct from `notesRequiredMessageKey`, which
   * the submit toast uses and is intentionally un-gated.
   */
  get inlineRequiredMessageKey(): string {
    if (this.showNotesError && this.showAttachmentError) return 'SDR_NOTE_AND_ATTACHMENT_REQUIRED';
    if (this.showAttachmentError) return 'SDR_ATTACHMENT_REQUIRED';
    return 'SDR_NOTE_REQUIRED';
  }

  /**
   * The i18n key for the submit toast — resolved from which requirement(s) are
   * unmet regardless of touched state, so a submit attempt always reports the
   * real blocker even if the user never engaged the section. Both unmet →
   * combined message; otherwise the specific one.
   */
  get notesRequiredMessageKey(): string {
    if (this.notesUnmet && this.attachmentsUnmet) return 'SDR_NOTE_AND_ATTACHMENT_REQUIRED';
    if (this.attachmentsUnmet) return 'SDR_ATTACHMENT_REQUIRED';
    return 'SDR_NOTE_REQUIRED';
  }

  get attachmentTitle(): string {
    return this.stagedAttachment?.file.name ?? '';
  }

  /** Human-readable size of the staged attachment (e.g. "1.4 MB"). */
  get attachmentSizeLabel(): string {
    return formatFileSize(this.stagedAttachment?.file.size ?? 0);
  }

  onNoteTextChange(value: string) {
    this.newNoteText = value ?? '';
    this.touchedNotes = true;
    this.buildSectionHeader();
  }

  triggerAttachPicker() {
    if (!this.isAttachmentEnabled) return;
    // Do NOT mark attachments touched here — merely opening the picker must not
    // flash the attachment-required helper. Touched flips only if the picker is
    // dismissed without a file (onAttachPickerCancel) or a staged attachment is
    // later removed.
    this.attachInput?.nativeElement.click();
  }

  /**
   * Fired by the file input's native `cancel` event — the user opened the
   * picker and dismissed it without choosing a file. Only now does the unmet
   * attachment requirement surface.
   */
  onAttachPickerCancel() {
    if (!this.isAttachmentEnabled) return;
    this.touchedAttachment = true;
    this.cdRef.markForCheck();
  }

  async loadImageFromDevice(event: Event) {
    const file = (event.target as HTMLInputElement)?.files?.[0];
    if (!file) return;
    const staged = await this._validateAndReadFile(file);
    if (!staged) {
      this.clearStagedAttachment();
      return;
    }
    this.stagedAttachment = staged;
    this.buildSectionHeader();
    this.cdRef.markForCheck();
  }

  /** Attach picker while editing an already-added staged note (pencil icon). */
  async loadEditImageFromDevice(event: Event) {
    const file = (event.target as HTMLInputElement)?.files?.[0];
    if (!file) return;
    const staged = await this._validateAndReadFile(file);
    if (!staged) return;
    this.editingStagedAttachment = staged;
    this.cdRef.markForCheck();
  }

  /** Shared size/mime-type validation + base64 read for both the composer's
   *  Attach and the edit-mode Attach. Notifies and returns null on failure. */
  private async _validateAndReadFile(file: File): Promise<{ file: File; base64: string } | null> {
    if (file.size / 1000 >= MAXIMUM_NOTE_ATTACHMENT_SIZE) {
      this.notificationService.notify(
        this.translate.instant('MAXIMUM_NOTE_ATTACHMENT_SIZE_NOTIFICATION', { size: MAXIMUM_NOTE_ATTACHMENT_SIZE }),
        'SDR Notes',
        'top',
        ToastStyle.INFO,
      );
      return null;
    }
    if (!NOTE_ATTACHMENT_MIME_TYPES_SUPPORTED_REGEX.test(file.name)) {
      this.notificationService.notify(
        this.translate.instant('NOTE_ATTACHMENT_MIME_TYPE_NOT_SUPPORTED_NOTIFICATION'),
        'SDR Notes',
        'top',
        ToastStyle.INFO,
      );
      return null;
    }
    const dataUrl = (await toBase64(file)) as string;
    return { file, base64: dataUrl.replace(/^data:[^;]+;base64,/, '') };
  }

  removeStagedAttachment() {
    // Removing the only attachment should surface the requirement immediately.
    this.touchedAttachment = true;
    this.clearStagedAttachment();
  }

  saveNewNote() {
    if (this.isSaveDisabled) return;
    const payload: SdrNoteSavedPayload = { text: this.newNoteText.trim() };
    if (this.stagedAttachment) {
      payload.attachment = {
        fileDataUrl: this.stagedAttachment.base64,
        mimeType: this.stagedAttachment.file.type,
        name: this.stagedAttachment.file.name,
        size: this.stagedAttachment.file.size,
      };
    }
    // Create flow: stash the note locally so it rides the submit batch later.
    // Detail flow: parent handles the noteSaved emission via dataService.updateNote;
    // the composer isn't rendered there, so this path isn't reached.
    const stagedShape = this.composerToStaged(this.newNoteText, this.stagedAttachment);
    this.staged.push(stagedShape);
    this.stagedNotesView.push(this.toIoNoteView(stagedShape, this.staged.length - 1));
    this.stagedNoteAdded.emit({ index: this.staged.length - 1, note: stagedShape });
    this.noteSaved.emit(payload);
    // Committing via Add is an explicit assertion of intent — surface any
    // requirement still unmet after this note is staged (e.g. text-only note
    // added while an attachment is mandatory).
    this.touchedNotes = true;
    this.touchedAttachment = true;
    this.newNoteText = '';
    this.clearStagedAttachment();
  }

  /** Pencil icon on a staged entry — opens its text AND attachment inline for
   *  editing. `editingStagedAttachment` starts `undefined` (unchanged) so
   *  `editingAttachmentView` falls back to showing the note's existing file
   *  until the user removes or replaces it. */
  startEditStagedNote(index: number): void {
    if (index < 0 || index >= this.staged.length) return;
    this.editingStagedIndex = index;
    this.editingStagedText = this.staged[index]?.noteText ?? '';
    this.editingStagedAttachment = undefined;
  }

  cancelEditStagedNote(): void {
    this.editingStagedIndex = null;
    this.editingStagedText = '';
    this.editingStagedAttachment = undefined;
    if (this.editAttachInput?.nativeElement) {
      this.editAttachInput.nativeElement.value = '';
    }
  }

  saveEditStagedNote(index: number): void {
    if (index < 0 || index >= this.staged.length) return;
    const target = this.staged[index];
    target.noteText = (this.editingStagedText ?? '').trim();
    if (this.editingStagedAttachment === null) {
      // Explicitly removed, no replacement chosen.
      target.attachment = undefined;
      this.touchedAttachment = true;
    } else if (this.editingStagedAttachment) {
      // Replacement file chosen.
      target.attachment = {
        documentBody: this.editingStagedAttachment.base64,
        fileName: this.editingStagedAttachment.file.name,
        mimeType: this.editingStagedAttachment.file.type,
        fileSize: this.editingStagedAttachment.file.size,
      };
      this.touchedAttachment = true;
    }
    // else undefined — attachment untouched, leave target.attachment as-is.
    const attachmentCleared = this.editingStagedAttachment === null;
    this.stagedNotesView[index] = this.toIoNoteView(target, index);
    // Emit BEFORE cancelEditStagedNote() — that resets editingStagedAttachment,
    // which is what attachmentCleared is derived from.
    this.stagedNoteEdited.emit({ index, note: target, attachmentCleared });
    this.cancelEditStagedNote();
    this.touchedNotes = true;
    this.buildSectionHeader();
    this.cdRef.markForCheck();
  }

  /** × on the edit-mode attachment chip — stages a removal (doesn't touch
   *  `staged[index]` until Save, so Cancel discards it). */
  removeEditingAttachment(): void {
    this.editingStagedAttachment = null;
    if (this.editAttachInput?.nativeElement) {
      this.editAttachInput.nativeElement.value = '';
    }
    this.cdRef.markForCheck();
  }

  triggerEditAttachPicker(): void {
    if (!this.isAttachmentEnabled) return;
    this.editAttachInput?.nativeElement.click();
  }

  /** What the edit-mode attachment chip should show for a given staged note:
   *  the newly-picked replacement, nothing (explicitly removed), or the
   *  note's current attachment (untouched so far). */
  editingAttachmentView(note: IONote): { name: string; sizeLabel: string; isImage: boolean } | null {
    if (this.editingStagedAttachment === null) return null;
    if (this.editingStagedAttachment) {
      return {
        name: this.editingStagedAttachment.file.name,
        sizeLabel: formatFileSize(this.editingStagedAttachment.file.size),
        isImage: this.editingStagedAttachment.file.type.startsWith('image/'),
      };
    }
    if (note?.hasDocument) {
      return { name: note.documentName, sizeLabel: this.noteSizeLabel(note), isImage: this.isImageNote(note) };
    }
    return null;
  }

  /** Trash icon on a staged entry. Deleting can leave a mandatory requirement
   *  unmet — engage both touched flags so the helper reflects the new state. */
  deleteStagedNote(index: number): void {
    if (index < 0 || index >= this.staged.length) return;
    this.touchedNotes = true;
    this.touchedAttachment = true;
    this.stagedNoteRemoved.emit(index);
    this.staged.splice(index, 1);
    this.stagedNotesView.splice(index, 1);
    this.reindexStagedView();
    if (this.editingStagedIndex === index) this.cancelEditStagedNote();
    this.buildSectionHeader();
    this.cdRef.markForCheck();
  }

  /** Build the `IONote` view-shape (owner, timestamp, attachment fields) from
   * a `StagedSdrNote`. Synthetic `noteId` is `staged-<index>` so the io-note
   * events route back here by index. */
  /**
   * Builds the `IONote` view-shape from a `StagedSdrNote`. Uses the note's
   * real annotation id when it has one (`existingAnnotationId` — set for a
   * note seeded from an existing Draft SDR being resumed, via
   * `initialStagedNotes`) so downstream consumers like
   * `downloadNoteAttachment` (which calls `fileService.getDocumentBody(note
   * .noteId)`) hit the real Dynamics record instead of a bogus synthetic id.
   * Falls back to the synthetic `staged-<index>` id for a genuinely new note
   * added this session, unchanged from before.
   */
  private toIoNoteView(staged: StagedSdrNote, index: number): IONote {
    const view = new IONote({
      annotationid: staged.existingAnnotationId ?? `staged-${index}`,
      notetext: staged.noteText ?? '',
      isdocument: !!staged.attachment,
      filename: staged.attachment?.fileName ?? '',
      filesize: staged.attachment?.fileSize ?? 0,
      mimetype: staged.attachment?.mimeType ?? '',
      documentbody: staged.attachment?.documentBody ?? '',
    });
    view.ownerName = this.authService.user?.displayName ?? '';
    view.createdTime = new Date();
    return view;
  }

  /** Re-stamp `staged-<index>` IDs after a splice so future events still resolve. */
  private reindexStagedView(): void {
    this.stagedNotesView = this.stagedNotesView.map((view, i) => {
      view.noteId = `staged-${i}`;
      return view;
    });
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByNoteId(_: number, note: IONote): string {
    return note.noteId;
  }

  // ──────────────── Read-only entry display (submitted SDR) ────────────────
  // io-note (the shared notes component used app-wide) has no avatar/FILE-ONLY-
  // badge markup and is used in 20+ other features, so extending it here would
  // risk regressing those. Existing (submitted) notes render via a dedicated
  // template instead; staged (create-flow) notes keep using <io-note> above,
  // since that's still where its own edit/delete affordances would live.

  /** Same date+time formatting `io-note` uses (EditableNote.getFormattedDateTime),
   *  kept in sync so both surfaces read identically. */
  getFormattedDateTime(value: Date): string {
    if (!value || isNaN(new Date(value).getTime())) return '';
    const day = this.datePipe.transform(value, this.dateTimeFormatsService.date, undefined, this.translate.currentLang);
    const time = new Date(value).toLocaleTimeString('en-US', {
      hour12: this.dateTimeFormatsService.is12HourFormat,
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${day} ${time}`;
  }

  /** An attachment with no note text — surfaces the "FILE ONLY" badge. */
  isFileOnlyNote(note: IONote): boolean {
    return !note?.noteText?.trim() && !!note?.hasDocument;
  }

  isImageNote(note: IONote): boolean {
    return !!note?.documentMimeType?.startsWith('image/');
  }

  noteSizeLabel(note: IONote): string {
    return formatFileSize(note?.documentSize ?? 0);
  }

  /**
   * Download a submitted note's attachment. Ported from EditableNote
   * (io-note.ts) `defaultDownloadAttachment`/`downloadDocumentBody` — same
   * logic (documentDataURL when already in hand, else fetch the document body
   * from Dynamics by annotation id), since io-note itself isn't reused here.
   */
  async downloadNoteAttachment(note: IONote): Promise<void> {
    if (!note?.documentName) return;
    if (this.deviceService.isOffline) {
      this.notificationService.notify(
        this.translate.instant('ATTACHMENT_CAN_BE_DOWNLOADED_ONLY_WHEN_ONLINE_MSG'),
        'SDR Notes',
        'top',
        ToastStyle.INFO,
      );
      return;
    }
    if (note.documentDataURL) {
      this.triggerBrowserDownload(note.documentName, this.toDataUrl(note.documentDataURL, note.documentMimeType), note.documentMimeType);
      return;
    }
    try {
      await this.uiService.displayLoader();
      const response = await this.fileService.getDocumentBody(note.noteId);
      const body = response?.['documentbody'];
      if (body) {
        this.triggerBrowserDownload(note.documentName, this.toDataUrl(body, note.documentMimeType), note.documentMimeType);
      }
    } catch (err) {
      console.error('downloadNoteAttachment failed', err);
    } finally {
      await this.uiService.dismissLoader();
    }
  }

  private toDataUrl(body: string, mimeType: string): string {
    if (body.indexOf('data:') === 0 || !mimeType) return body;
    return `data:${mimeType};base64,${body}`;
  }

  private triggerBrowserDownload(fileName: string, dataUrl: string, mimeType: string): void {
    if (this.deviceService.isNativeApp && !this.deviceService.deviceFlags.electron) {
      this.fileService.downloadeBase64DataFileAndOpenInNativeApp(fileName, dataUrl, mimeType);
      return;
    }
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = fileName;
    link.click();
    link.remove();
  }

  private clearStagedAttachment() {
    this.stagedAttachment = null;
    if (this.attachInput?.nativeElement) {
      this.attachInput.nativeElement.value = '';
    }
    this.buildSectionHeader();
    this.cdRef.markForCheck();
  }
}
